import { IGatsbySvg } from "@bond-london/gatsby-transformer-extracted-svg";
import classNames from "classnames";
import concat from "lodash/concat";
import flatMap from "lodash/flatMap";
import uniq from "lodash/uniq";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select, { StylesConfig } from "react-select";
import { PagesContext } from "../contexts/PagesContext";

interface CategoryOptionType {
  label: string;
  value: string;
}

const customStyles: StylesConfig<CategoryOptionType, false> = {
  option: (provided, state) => ({
    ...provided,
    color: state.isSelected ? "white" : "#5752d1",
    fontWeight: state.isSelected ? "bold" : "normal",
    padding: 20,
    backgroundColor: state.isSelected ? "#5752d1" : "white",
    fontFamily: "IBM Plex Sans",
    "&:hover": { background: "rgba(87, 82, 209, 0.2)", color: "#5752d1" },
  }),
  control: () => ({
    width: "100%",
    border: 0,
    display: "flex",
    justifyContent: "space-between",
    fontSize: "1.75rem",
    color: "#5752d1",
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = "opacity 300ms";
    const otherSingleValueStyles = {
      color: "#5752d1",
      fontSize: "1.75rem",
    };
    return { ...provided, opacity, transition, ...otherSingleValueStyles };
  },
  input: (provided) => ({
    ...provided,
    border: "0",
    color: "#5752d1",
    "&:focus": {
      outline: "none",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#5752d1",
    fontSize: "1.75rem",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: () => ({
    fill: "#5752d1",
    width: "40px",
  }),
};

export const PageFilter: React.FC<{
  filterText: string;
  shape?: IGatsbySvg | null;
  onFilterChanged: (filter: Queries.GraphCMS_Category | undefined) => void;
}> = ({ filterText, shape, onFilterChanged }) => {
  const { t } = useTranslation();
  const { cmsPages } = useContext(PagesContext);
  const categories = useMemo(
    () => concat("", uniq(flatMap(cmsPages, (p) => p.pageCategory))),
    [cmsPages],
  );
  const selectOptions = categories.map((category) => {
    return { value: t(category), label: t(category) };
  });
  return (
    <section
      className="page-grid relative col-span-full col-start-1 inline-block"
      data-component="Page Filter"
    >
      {shape && (
        <img
          src={shape.encoded ?? shape.encodedUrl}
          alt=""
          className={classNames(
            "col-span-4 col-start-2 md:col-span-4 md:col-start-3",
            "row-span-4 row-start-1",
          )}
        />
      )}
      <h3 className="h2 col-span-4 col-start-2 row-start-2 pt-5 md:col-span-6 md:col-start-3">
        {filterText}
      </h3>
      <div className="col-span-3 col-start-2 row-start-3 flex pt-5 md:col-start-6 md:row-start-2 lg:col-span-6 lg:col-start-9">
        <div className="flex flex-grow flex-col">
          <Select
            options={selectOptions}
            styles={customStyles}
            onChange={(e) =>
              onFilterChanged(
                e
                  ? (e.value as unknown as Queries.GraphCMS_Category)
                  : undefined,
              )
            }
          />
          <div className="dashed-line col-span-6 col-start-9 row-start-2 h-10px text-hyacinth"></div>
        </div>
      </div>
    </section>
  );
};
